import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button, DataSection, FieldError, FieldRow, Input, Label, sentinel } from '@sentinel/ui';
import { emailSchema, type EmailInput } from '../schema';
import { useRequestEmailChange } from '../use-profile-mutations';
import type { PendingEmailChange, SectionFeedback } from '../types';
import { getStatus } from '../error-accessors';

export interface EmailSectionProps {
  currentEmail: string;
  pending: PendingEmailChange | null;
  onPending: (p: PendingEmailChange | null) => void;
  feedback: SectionFeedback;
}

const pendingStyle: React.CSSProperties = {
  display: 'grid',
  gap: '0.5rem',
  justifyItems: 'start',
};

export function EmailSection({ currentEmail, pending, onPending, feedback }: EmailSectionProps) {
  const { t } = useTranslation('profile');
  const request = useRequestEmailChange();
  const form = useForm<EmailInput>({
    resolver: zodResolver(emailSchema),
    defaultValues: { new_email: '' },
  });

  async function onSubmit(values: EmailInput) {
    feedback.clear();
    const newEmail = values.new_email.trim();
    if (newEmail.toLowerCase() === currentEmail.trim().toLowerCase()) {
      form.setError('new_email', { message: t('email_same') });
      return;
    }
    try {
      await request.mutateAsync({ new_email: newEmail });
      onPending({ address: newEmail });
    } catch (e) {
      const status = getStatus(e);
      // The step-up middleware already ran the dialog and replayed the
      // request on success, so a 403 reaching here means the user cancelled
      // step-up. Leave the form as it is and say nothing.
      if (status === 403) return;
      if (status === 409) {
        form.setError('new_email', { message: t('email_taken') });
        return;
      }
      if (status === 429) {
        // This limiter is a fixed five-per-hour window keyed by the target
        // address, not the short per-IP one, so it gets its own copy.
        feedback.error(t('email_rate_limited'));
        return;
      }
      if (status === 400) {
        form.setError('new_email', { message: t('email_invalid') });
        return;
      }
      feedback.error(t('generic_error'));
    }
  }

  const rawEmailError = form.formState.errors.new_email?.message;
  const emailErrorMsg = rawEmailError === 'email_invalid' ? t('email_invalid') : rawEmailError;

  return (
    <DataSection
      testId="profile-email"
      title={t('email_section_title')}
      description={t('email_section_body')}
    >
      {pending ? (
        <div data-testid="profile-email-pending" style={pendingStyle}>
          <strong>{t('email_pending_title')}</strong>
          <p style={{ margin: 0 }}>
            {pending.address
              ? t('email_pending_body', { email: pending.address })
              : t('email_pending_body_unknown')}
          </p>
          {/* ConfirmEmailChangeUseCase revokes every session after applying the
              change, so opening the link ends this browser's session too. Say it
              here rather than letting the user discover it at the login screen.
              textMuted, not textSubtle: DataSection's card sits on surfaceElevated,
              and textSubtle's 0.47 alpha blends to a 4.34:1 ratio there — under the
              4.5:1 AA floor for 13px text, caught by axe on this pending panel. */}
          <p style={{ margin: 0, fontSize: 13, color: sentinel.textMuted }}>
            {t('email_pending_signout')}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="profile-email-pending-restart"
            onClick={() => {
              feedback.clear();
              onPending(null);
            }}
          >
            {t('email_pending_restart')}
          </Button>
        </div>
      ) : (
        <>
          <p data-testid="profile-email-current">
            <span>{t('email_current_label')}</span> {currentEmail}
          </p>
          <form
            data-testid="profile-email-form"
            onSubmit={form.handleSubmit(onSubmit)}
            style={{ display: 'grid', gap: '0.75rem' }}
          >
            <FieldRow>
              <Label htmlFor="profile-email-input">{t('email_label')}</Label>
              <Input
                id="profile-email-input"
                data-testid="profile-email-input"
                type="email"
                autoComplete="email"
                aria-invalid={form.formState.errors.new_email ? 'true' : undefined}
                {...form.register('new_email')}
              />
              <FieldError message={emailErrorMsg} />
            </FieldRow>
            <Button type="submit" data-testid="profile-email-submit" disabled={request.isPending}>
              {t('email_submit')}
            </Button>
          </form>
        </>
      )}
    </DataSection>
  );
}
